import React, { useState, useRef, useEffect } from "react";

import { Bell, Calendar, Menu, LogOut, Trash2 } from "lucide-react";

import CommonButton from "../../components/CommonButton";

import DateFilterPopover, {
  type FilterMode,
} from "../../components/Admin/DateFilterPopover";

import { format } from "date-fns/format";
interface HeaderProps {
  toggleSidebar: () => void;
  onFilterChange: (filterData: {
    mode: FilterMode;
    displayLabel: string;
    value: any;
  }) => void;
  onSignOut?: () => void;
  onClearCache?: () => void;
  isClearingCache?: boolean;
  isSigningOut?: boolean;
}

const AdminHeader: React.FC<HeaderProps> = ({
  toggleSidebar,
  onFilterChange,
  onSignOut,
  onClearCache,
  isClearingCache = false,
  isSigningOut = false,
}) => {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [dateButtonLabel, setDateButtonLabel] = useState("This Week");
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);

  // Close dropdown menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        profileRef.current &&
        !profileRef.current.contains(event.target as Node)
      ) {
        setIsProfileOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleFilterApply = (filterData: {
    mode: FilterMode;
    displayLabel: string;
    value: any;
  }) => {
    setDateButtonLabel(filterData.displayLabel);
    setIsCalendarOpen(false);

    let fromDate: string | undefined;
    let toDate: string | undefined;

    // ------------------------------------------------
    // Dashboard dates are calendar dates.
    //
    // IMPORTANT:
    // Do NOT use toISOString() here.
    //
    // toISOString() converts the browser date to UTC,
    // which can shift the calendar date when the
    // business timezone is UAE.
    // ------------------------------------------------

    if (filterData.value) {
      if (filterData.value.start) {
        fromDate = format(new Date(filterData.value.start), "yyyy-MM-dd");
      }

      if (filterData.value.end) {
        toDate = format(new Date(filterData.value.end), "yyyy-MM-dd");
      }

      // Single day
      if (filterData.mode === "day" && filterData.value instanceof Date) {
        fromDate = format(filterData.value, "yyyy-MM-dd");

        toDate = fromDate;
      }
    }

    onFilterChange({
      mode: filterData.mode,

      displayLabel: filterData.displayLabel,

      value: {
        raw: filterData.value,
        fromDate,
        toDate,
      },
    });
  };

  const handleClearCacheClick = () => {
    setIsProfileOpen(false);
    if (onClearCache) {
      onClearCache();
    }
  };

  const handleSignOutClick = () => {
    setIsProfileOpen(false);
    if (onSignOut) {
      onSignOut();
    }
  };

  return (
    <header
      className="
        fixed top-0 right-0 left-0
        md:left-[250px]
        h-[70px]
        bg-[#FFFFFF]
        border-b
        flex items-center justify-between
        px-4 md:px-6
        z-40
      "
    >
      {/* Left */}
      <div className="flex items-center gap-3">
        <button className="md:hidden text-[#2F5E2E]" onClick={toggleSidebar}>
          <Menu size={24} />
        </button>

        <div className="hidden md:block">
          <p className="text-logoHeading text-lg">Welcome, Admin</p>
        </div>
      </div>

      {/* Right */}
      <div className="flex items-center gap-5">
        {/* Calendar Button */}
        <div className="relative">
          <div className="hidden lg:block">
            <CommonButton
              icon={<Calendar size={16} />}
              iconPosition="left"
              text={dateButtonLabel}
              variant="outline"
              rounded="md"
              onClick={() => setIsCalendarOpen(!isCalendarOpen)}
            />
          </div>

          <div className="lg:hidden">
            <CommonButton
              icon={<Calendar size={22} />}
              variant="outline"
              rounded="md"
              className="border-none"
              onClick={() => setIsCalendarOpen(!isCalendarOpen)}
            />
          </div>

          {isCalendarOpen && (
            <DateFilterPopover
              onClose={() => setIsCalendarOpen(false)}
              onApply={handleFilterApply}
            />
          )}
        </div>

        <div>
          <Bell size={26} className="text-iconColor cursor-pointer" />
        </div>

        {/* Profile Avatar & Dropdown */}
        <div className="relative" ref={profileRef}>
          <button
            onClick={() => setIsProfileOpen(!isProfileOpen)}
            className="w-9 h-9 rounded-full bg-[#6DBE45] text-white flex items-center justify-center font-semibold focus:outline-none focus:ring-2 focus:ring-[#6DBE45] focus:ring-offset-2 transition-transform active:scale-95"
          >
            A
          </button>

          {/* Popover Menu */}
          {isProfileOpen && (
            <div className="absolute right-0 mt-2 w-56 bg-white rounded-2xl shadow-xl border border-gray-100 py-3 z-50 animate-in fade-in slide-in-from-top-2 duration-150">
              <div className="px-4 pb-2 border-b border-gray-100">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  My Account
                </p>
              </div>

              <div className="py-1">
                <button
                  onClick={handleClearCacheClick}
                  disabled={isClearingCache}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-[#F2F9EE] hover:text-[#2F5E2E] transition-colors disabled:opacity-50"
                >
                  <Trash2 size={18} className="text-gray-500" />
                  {isClearingCache ? "Clearing Cache..." : "Clear All Cache"}
                </button>

                <button
                  onClick={handleSignOutClick}
                  disabled={isSigningOut}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50"
                >
                  <LogOut size={18} className="text-red-500" />
                  {isSigningOut ? "Signing Out..." : "Signout"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default AdminHeader;
